import {defineStore} from 'pinia'
import {ref, readonly, reactive, computed} from 'vue'
import type { Field, LabelHeadItem } from 'src/biz_spa/domains/analysis/types'
import type { searchQueriesCrossAnalysisTypes } from 'src/biz_spa/domains/analysis/consts'
import axios from 'axios'
import {castAttributesForCamelCase, castAttributesForSnakeCase} from 'src/biz_spa/modules/string_converter'
import { useFlash } from 'src/biz_spa/composables'

type SearchKeywordImpressionAggregate = {
  fields: Field[]
  impressionCount: number
  locale: string
  originalLocale: string,
  searchKeywordImpressionsBranded: LabelHeadItem[]
  searchKeywordImpressionsSelected: LabelHeadItem[]
}

type SearchKeywordImpressionPlot = {
  fields: Field[]
  impressionCount: {
    [key: string]: number
  }
}

export type ReadSearchKeywordImpressionsAggregatesParams = {
  analysisAxis: typeof searchQueriesCrossAnalysisTypes[number]['id']
  storeUuids?: string[]
  locales?: string[]
  brandIds?: number[]
  franchiseeIds?: number[]
  areaIds?: number[]
  userCompanyIds?: number[]
  prefectures?: number[]
  originalTagGroupATagIds?: number[]
  originalTagGroupBTagIds?: number[]
  originalTagGroupCTagIds?: number[]
  originalTagGroupDTagIds?: number[]
  originalTagGroupETagIds?: number[]
  searchKeywordLabelBranded?: string[]
  searchKeywordLabelSelected?: string[]
  startTime: string
  endTime: string
  sortKey?: string
  sortOrder?: string
  page?: number
  per?: number
  valueGteq?: number | null
  valueLteq?: number | null
}

export type GetSearchKeywordImpressionsAggregatesCountParams = Omit<ReadSearchKeywordImpressionsAggregatesParams, 'analysisAxis' | 'sortKey' | 'sortOrder' | 'page' | 'per'>

export type ReadSearchKeywordImpressionsPlotsParams = {
  analysisAxis: typeof searchQueriesCrossAnalysisTypes[number]['id']
  analysisAxisId: string
  locales?: string[]
  brandIds?: number[]
  franchiseeIds?: number[]
  areaIds?: number[]
  userCompanyIds?: number[]
  prefectures?: number[]
  originalTagGroupATagIds?: number[]
  originalTagGroupBTagIds?: number[]
  originalTagGroupCTagIds?: number[]
  originalTagGroupDTagIds?: number[]
  originalTagGroupETagIds?: number[]
  searchKeywordLabelBranded?: string[]
  searchKeywordLabelSelected?: string[]
  storeUuids?: string[]
  startTime: string
  endTime: string
}

type Aggregates = SearchKeywordImpressionAggregate[]
type Plots = SearchKeywordImpressionPlot[]

export const useCompaniesAnalysisSearchQueriesStore = defineStore('companiesAnalysisSearchQueries', () => {
  const { updateFlash } = useFlash()
  const aggregatesLoading = ref<boolean>(true)
  const plotsLoading = computed<boolean>(() => plots.length === 0)
  const rawAggregates: Aggregates = reactive([])
  const plots: Plots = reactive([])
  const totalCount = ref<number>(0)
  const countOnThisPage = computed<number>(() => rawAggregates.length)
  const all: SearchKeywordImpressionAggregate = reactive({
    fields: [],
    impressionCount: 0,
    locale: '',
    originalLocale: '',
    searchKeywordImpressionsBranded: [],
    searchKeywordImpressionsSelected: [],
  })
  const aggregates = computed<Aggregates>(() => {
    if (aggregatesLoading.value) return []
    return [all, ...rawAggregates]
  })

  const readSearchKeywordImpressionsPlots = async ({ companyUuid, params }: { companyUuid: string, params: ReadSearchKeywordImpressionsPlotsParams }) => {

    return axios
      .post(`/api/companies/${companyUuid}/analysis/search_keyword_impressions/graph_elements`,
        castAttributesForSnakeCase(params)
      )
      .then(res => {
        plots.push(castAttributesForCamelCase(res.data.data) as SearchKeywordImpressionPlot)
      })
      .catch(err => {
        updateFlash({ type: 'alert', message: err.response.data.errors[0].detail })
      })
  }

  const readSearchKeywordImpressionsAggregates = async ({ companyUuid, params, resetPlots }: { companyUuid: string, params: ReadSearchKeywordImpressionsAggregatesParams, resetPlots: boolean }) => {
    aggregatesLoading.value = true
    if (resetPlots) {
      plots.length = 0
    }

    return axios
      .post(`/api/companies/${companyUuid}/analysis/search_keyword_impressions/aggregates`,
        castAttributesForSnakeCase(params)
      )
      .then((res) => {
        rawAggregates.length = 0
        res.data.data.forEach((aggregate: SearchKeywordImpressionAggregate) => {
          rawAggregates.push(castAttributesForCamelCase(aggregate) as SearchKeywordImpressionAggregate)
        })
        const meta = castAttributesForCamelCase(res.data.meta)
        const tmp = meta.all as SearchKeywordImpressionAggregate
        Object.assign(all, tmp)

        // allを除いたデータ数が帰ってくる
        totalCount.value = meta.totalCount as number
      })
      .catch(err => {
        updateFlash({ type: 'alert', message: err.response.data.errors[0].detail })
      })
      .finally(() => {
        aggregatesLoading.value = false
      })
  }

  const getSearchKeywordImpressionsAggregatesCount = async ({ companyUuid, params }: { companyUuid: string, params: GetSearchKeywordImpressionsAggregatesCountParams }): Promise<number> => {
    const response = await axios
      .post(`/api/companies/${companyUuid}/analysis/search_keyword_impressions/count`, castAttributesForSnakeCase(params))
    return response.data.data
  }

  return {
    aggregatesLoading: readonly(aggregatesLoading),
    plotsLoading: readonly(plotsLoading),
    aggregates: readonly(aggregates),
    plots: readonly(plots),
    totalCount: readonly(totalCount),
    all: readonly(all),
    countOnThisPage: readonly(countOnThisPage),
    readSearchKeywordImpressionsAggregates,
    getSearchKeywordImpressionsAggregatesCount,
    readSearchKeywordImpressionsPlots,
  }
})
